/**
 * Admin → Usuários (Alunos)
 *
 * Tela para gerenciar os ALUNOS (usuário comum, aluno validado, aluno premium).
 * Ações disponíveis:
 *   - Ver detalhes / editar papel e nível
 *   - Validar cadastro
 *   - Bloquear / desbloquear
 *   - Resetar senha (auto gerada OU manual definida pelo Master), marcando
 *     `must_change_password=true` para forçar troca no próximo login do aluno.
 *
 * A gestão de EQUIPE (Gestor Master, Operador, Comercial) fica em /admin/gestao-acessos.
 */
import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Search, Eye, EyeOff, Shield, Ban, CheckCircle2, KeyRound,
  Copy, AlertTriangle, Sparkles, RefreshCw, Stethoscope, XCircle,
  Calendar, Clock, MapPin, GraduationCap, Pencil, Wand2, ArrowRight,
  Lock, Unlock, Trash2,
} from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";

// ============================================================================
// CredentialsCard — mostra a senha temp gerada/definida (padrão UX consistente
// com AdminGestaoAcessos)
// ============================================================================
function CredentialsCard({ data, onClose }) {
  const [showPwd, setShowPwd] = useState(false);
  const copy = async (text, label) => {
    const ok = await copyToClipboard(text);
    if (ok) toast.success(`${label} copiado`);
    else toast.error(`Não foi possível copiar — copie manualmente: ${text}`);
  };
  return (
    <Card className="bg-amber-50 border-amber-300 border-2">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-2">
          <Sparkles className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900">Senha provisória — copie agora</p>
            <p className="text-xs text-amber-800 mt-0.5">
              Envie ao aluno por canal seguro (WhatsApp pessoal ou em mãos). Ele
              será <strong>obrigado a trocar a senha no primeiro acesso</strong>.
            </p>
          </div>
        </div>
        <div className="space-y-2 bg-white rounded-lg p-3 border border-amber-200">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase text-muted-foreground font-medium tracking-wider">E-mail</p>
              <p className="font-mono text-sm break-all" data-testid="creds-email">{data.email}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => copy(data.email, "E-mail")} data-testid="creds-email-copy">
              <Copy className="w-3 h-3" />
            </Button>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase text-muted-foreground font-medium tracking-wider">Senha provisória</p>
              <p className="font-mono text-sm" data-testid="creds-password">
                {showPwd ? data.password : "•".repeat(data.password?.length || 10)}
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowPwd((s) => !s)} data-testid="creds-password-toggle">
              {showPwd ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            </Button>
            <Button size="sm" variant="outline" onClick={() => copy(data.password, "Senha")} data-testid="creds-password-copy">
              <Copy className="w-3 h-3" />
            </Button>
          </div>
        </div>
        <Button onClick={onClose} className="w-full" data-testid="creds-confirm">
          Já copiei — entendi que não verei novamente
        </Button>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// ResetStudentPasswordDialog — modal de reset de senha para alunos
// Duas opções:
//   - "auto":   gera senha forte no backend e retorna 1× (POST /reset-password)
//   - "manual": admin digita a senha provisória (POST /set-password)
// Em ambos os casos o backend seta must_change_password=true no aluno.
// ============================================================================
function ResetStudentPasswordDialog({ user, open, onClose }) {
  const [mode, setMode] = useState("auto"); // auto | manual
  const [manualPwd, setManualPwd] = useState("");
  const [showManualPwd, setShowManualPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creds, setCreds] = useState(null);

  const reset = () => {
    setMode("auto");
    setManualPwd("");
    setShowManualPwd(false);
    setLoading(false);
    setCreds(null);
  };

  const doReset = async () => {
    if (mode === "manual" && manualPwd.trim().length < 6) {
      toast.error("A senha provisória deve ter pelo menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      if (mode === "auto") {
        const { data } = await api.post(`/admin/users/${user.id}/reset-password`);
        setCreds({ email: data.email, password: data.temp_password });
        toast.success("Senha provisória gerada");
      } else {
        await api.post(`/admin/users/${user.id}/set-password`, { new_password: manualPwd });
        setCreds({ email: user.email, password: manualPwd });
        toast.success("Senha provisória definida");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao resetar senha");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}
    >
      <DialogContent className="max-w-md rounded-2xl" data-testid="reset-student-dialog">
        <DialogHeader>
          <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center mb-1">
            <KeyRound className="w-5 h-5" />
          </div>
          <DialogTitle className="font-display">Resetar senha do aluno</DialogTitle>
          <DialogDescription className="leading-relaxed">
            Gera uma <strong>senha provisória</strong> para{" "}
            <span className="text-[hsl(var(--primary))] font-medium">{user?.full_name}</span>.
            No próximo login o aluno será obrigado a definir uma senha definitiva.
          </DialogDescription>
        </DialogHeader>

        {creds ? (
          <CredentialsCard data={creds} onClose={() => { reset(); onClose(); }} />
        ) : (
          <div className="space-y-3">
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-3 text-xs text-amber-900 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <p>
                  A senha atual do aluno será <strong>invalidada imediatamente</strong>.
                  Se ele estiver logado, será desconectado no próximo refresh.
                </p>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Como definir a nova senha?</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setMode("auto")}
                  className={`h-auto py-2.5 px-3 rounded-lg border-2 text-left transition-all ${
                    mode === "auto"
                      ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10"
                      : "border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]/50"
                  }`}
                  data-testid="reset-mode-auto"
                >
                  <div className="flex items-center gap-1.5">
                    <RefreshCw className="w-3.5 h-3.5 text-[hsl(var(--accent))]" />
                    <span className="text-xs font-semibold">Gerar automática</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1 leading-tight">
                    Senha forte de 10 caracteres
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setMode("manual")}
                  className={`h-auto py-2.5 px-3 rounded-lg border-2 text-left transition-all ${
                    mode === "manual"
                      ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10"
                      : "border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]/50"
                  }`}
                  data-testid="reset-mode-manual"
                >
                  <div className="flex items-center gap-1.5">
                    <KeyRound className="w-3.5 h-3.5 text-[hsl(var(--accent))]" />
                    <span className="text-xs font-semibold">Digitar senha</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1 leading-tight">
                    Você define manualmente
                  </p>
                </button>
              </div>
            </div>

            {mode === "manual" && (
              <div className="space-y-1.5">
                <Label className="text-xs" htmlFor="manual-pwd">Senha provisória (mínimo 6 caracteres)</Label>
                <div className="relative">
                  <Input
                    id="manual-pwd"
                    type={showManualPwd ? "text" : "password"}
                    value={manualPwd}
                    onChange={(e) => setManualPwd(e.target.value)}
                    placeholder="Ex.: CEAP@2026"
                    className="pr-9 font-mono"
                    data-testid="reset-manual-pwd-input"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowManualPwd((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[hsl(var(--primary))]"
                    aria-label={showManualPwd ? "Ocultar" : "Mostrar"}
                  >
                    {showManualPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            <Button
              onClick={doReset}
              disabled={loading || (mode === "manual" && manualPwd.trim().length < 6)}
              className="w-full bg-amber-600 hover:bg-amber-700"
              data-testid="reset-pwd-confirm"
            >
              <KeyRound className="w-4 h-4 mr-1.5" />
              {loading
                ? "Processando…"
                : mode === "auto"
                  ? "Gerar senha provisória"
                  : "Definir senha provisória"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Utilitários de attendance (para o modal de detalhes)
// ============================================================================
const ATTENDANCE_STATUS_LABEL = {
  presenca_concluida: { label: "Presença concluída", tone: "emerald" },
  presenca_parcial:   { label: "Presença parcial",   tone: "amber"   },
  saida_pendente:     { label: "Saída pendente",     tone: "sky"     },
  ausente:            { label: "Ausente",            tone: "rose"    },
};

function fmtDateBR(dt) {
  if (!dt) return "—";
  const iso = typeof dt === "string" ? dt : "";
  const datePart = iso.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return dt;
  const [y, m, d] = datePart.split("-");
  return `${d}/${m}/${y}`;
}

function fmtTimeBR(dt) {
  if (!dt) return "—";
  try {
    const date = new Date(dt);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleTimeString("pt-BR", {
      hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo",
    });
  } catch { return "—"; }
}

// ============================================================================
// AttendanceSection — lista de frequência do aluno, com dropdown de curso
// ============================================================================
function AttendanceSection({ attendance }) {
  const [selectedCourse, setSelectedCourse] = useState("all");

  // Cursos únicos e sumário agregado
  const { courses, byCourse } = useMemo(() => {
    const map = new Map();
    for (const a of attendance || []) {
      const cid = a.course_id || "_no_course_";
      if (!map.has(cid)) {
        map.set(cid, {
          course_id: cid,
          course_name: a.course_name || "(curso removido)",
          course_city: a.course_city,
          course_state: a.course_state,
          course_workload_hours: a.course_workload_hours,
          sessions: [],
        });
      }
      map.get(cid).sessions.push(a);
    }
    // Ordenar cada curso pela data de sessão (mais recente primeiro)
    for (const c of map.values()) {
      c.sessions.sort((a, b) => (b.attendance_date || "").localeCompare(a.attendance_date || ""));
      c.total_hours = c.sessions.reduce((s, x) => s + (x.hours_attended || 0), 0);
      c.total_percent = c.sessions.reduce((s, x) => s + (x.attendance_percent || 0), 0);
    }
    const arr = Array.from(map.values()).sort((a, b) =>
      (b.sessions[0]?.attendance_date || "").localeCompare(a.sessions[0]?.attendance_date || "")
    );
    return { courses: arr, byCourse: map };
  }, [attendance]);

  const totalSessions = attendance?.length || 0;
  const active = selectedCourse !== "all" ? byCourse.get(selectedCourse) : null;

  return (
    <div>
      <div className="flex items-center justify-between mt-3 mb-2 gap-2">
        <p className="font-medium">
          Frequência
          <span className="text-muted-foreground text-xs font-normal ml-1.5">
            ({totalSessions} sessão{totalSessions !== 1 ? "es" : ""} em {courses.length} curso{courses.length !== 1 ? "s" : ""})
          </span>
        </p>
        {courses.length > 0 && (
          <Select value={selectedCourse} onValueChange={setSelectedCourse}>
            <SelectTrigger className="w-[260px] h-8 text-xs" data-testid="attendance-course-filter">
              <SelectValue placeholder="Selecionar curso" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os cursos</SelectItem>
              {courses.map((c) => (
                <SelectItem key={c.course_id} value={c.course_id}>
                  {c.course_name.length > 42 ? c.course_name.slice(0, 42) + "…" : c.course_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {totalSessions === 0 && (
        <p className="text-xs text-muted-foreground italic px-2 py-3 bg-[hsl(var(--secondary))]/40 rounded-lg">
          Este aluno ainda não tem nenhum registro de presença.
        </p>
      )}

      {/* Modo "Todos os cursos": resumo agregado por curso */}
      {selectedCourse === "all" && courses.length > 0 && (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="attendance-summary-all">
          {courses.map((c) => (
            <button
              key={c.course_id}
              type="button"
              onClick={() => setSelectedCourse(c.course_id)}
              className="w-full text-left p-2.5 rounded-lg border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/5 transition-colors"
              data-testid={`attendance-course-${c.course_id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs font-medium text-[hsl(var(--primary))] line-clamp-2 flex-1">
                  {c.course_name}
                </p>
                <Badge variant="secondary" className="text-[10px] shrink-0">
                  {c.sessions.length} sessão{c.sessions.length !== 1 ? "es" : ""}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-3 mt-1 text-[10px] text-muted-foreground">
                {c.course_city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {c.course_city}{c.course_state ? `/${c.course_state}` : ""}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {c.total_hours.toFixed(1)}h contabilizadas
                </span>
                {c.course_workload_hours && (
                  <span className="flex items-center gap-1">
                    <GraduationCap className="w-3 h-3" /> Carga: {c.course_workload_hours}h
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Modo curso específico: lista detalhada de sessões */}
      {active && (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="attendance-detail">
          <Card className="bg-[hsl(var(--secondary))]/40">
            <CardContent className="p-2.5">
              <p className="text-xs font-semibold text-[hsl(var(--primary))] line-clamp-2">
                {active.course_name}
              </p>
              <div className="flex flex-wrap gap-2 mt-1 text-[10px] text-muted-foreground">
                {active.course_city && (
                  <span>{active.course_city}{active.course_state ? `/${active.course_state}` : ""}</span>
                )}
                <span>Total: <strong>{active.total_hours.toFixed(1)}h</strong> em {active.sessions.length} sessão{active.sessions.length !== 1 ? "es" : ""}</span>
              </div>
            </CardContent>
          </Card>

          {active.sessions.map((s) => {
            const meta = ATTENDANCE_STATUS_LABEL[s.status] || { label: s.status, tone: "slate" };
            const toneClasses = {
              emerald: "bg-emerald-100 text-emerald-900 border-emerald-300",
              amber:   "bg-amber-100 text-amber-900 border-amber-300",
              sky:     "bg-sky-100 text-sky-900 border-sky-300",
              rose:    "bg-rose-100 text-rose-900 border-rose-300",
              slate:   "bg-slate-100 text-slate-800 border-slate-300",
            }[meta.tone];
            return (
              <div
                key={s.id}
                className="p-2.5 rounded-lg border border-[hsl(var(--border))] bg-white space-y-1"
                data-testid={`attendance-session-${s.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-xs">
                    <Calendar className="w-3.5 h-3.5 text-[hsl(var(--accent))]" />
                    <span className="font-medium text-[hsl(var(--primary))]">{fmtDateBR(s.attendance_date || s.created_at)}</span>
                  </div>
                  <Badge className={`text-[10px] ${toneClasses}`}>{meta.label}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
                  <div>
                    <span className="block text-[9px] uppercase tracking-wider">Entrada</span>
                    <span className="font-mono text-[hsl(var(--primary))]">{fmtTimeBR(s.checkin_at)}</span>
                  </div>
                  <div>
                    <span className="block text-[9px] uppercase tracking-wider">Saída</span>
                    <span className="font-mono text-[hsl(var(--primary))]">
                      {s.checkout_at ? fmtTimeBR(s.checkout_at) : <span className="text-amber-700">pendente</span>}
                    </span>
                  </div>
                  <div>
                    <span className="block text-[9px] uppercase tracking-wider">Horas</span>
                    <span className="text-[hsl(var(--primary))]"><strong>{(s.hours_attended || 0).toFixed(2)}h</strong></span>
                  </div>
                </div>
                {s.operator_name && (
                  <p className="text-[10px] text-muted-foreground italic mt-1">
                    Registrado por: {s.operator_name}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// EditNameDialog — modal de edição do nome completo do aluno.
//
// Motivação (ago/2026): muitos alunos cadastram o nome com iniciais minúsculas
// ou com erros de digitação. Como o nome aparece no certificado, o Gestor
// Master precisa de uma ferramenta rápida para corrigir e propagar a correção
// para os certificados já emitidos.
// ============================================================================
function EditNameDialog({ target, onClose, onSaved }) {
  const currentName = target?.full_name || "";
  const [value, setValue] = useState(currentName);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(currentName);
    setPreview("");
  }, [currentName]);

  const autoCorrect = async () => {
    if (!value.trim()) {
      toast.error("Digite um nome primeiro");
      return;
    }
    try {
      const { data } = await api.post("/admin/users/normalize-name", { name: value });
      setValue(data.normalized);
      setPreview(data.normalized);
      toast.success("Nome capitalizado automaticamente");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao normalizar nome");
    }
  };

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed.length < 3) {
      toast.error("Nome muito curto (mínimo 3 caracteres)");
      return;
    }
    if (trimmed === currentName) {
      toast.info("Nome não foi alterado");
      onClose();
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/users/${target.id}`, { full_name: trimmed });
      const cascadeCount = data?._cascade?.certificates_updated || 0;
      toast.success(
        cascadeCount > 0
          ? `Nome atualizado. ${cascadeCount} certificado(s) também foram atualizado(s).`
          : "Nome atualizado com sucesso"
      );
      onSaved?.(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const changed = value.trim() !== currentName && value.trim().length > 0;

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[520px] rounded-2xl" data-testid="edit-name-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-5 h-5 text-[hsl(var(--primary))]" />
            Editar nome do aluno
          </DialogTitle>
          <DialogDescription>
            Corrija a grafia do nome completo. O novo nome será propagado
            para <strong>todos os certificados já emitidos</strong> deste aluno,
            garantindo que a próxima geração do PDF saia com o nome correto.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Nome atual (readonly) */}
          <div className="rounded-lg border border-border bg-muted/50 p-3">
            <p className="text-[10px] uppercase text-muted-foreground font-medium tracking-wider mb-1">
              Nome atual
            </p>
            <p className="font-medium text-sm break-words" data-testid="edit-name-current">
              {currentName || "—"}
            </p>
          </div>

          {/* Input do novo nome */}
          <div className="space-y-2">
            <Label htmlFor="new-name">Novo nome completo</Label>
            <div className="flex gap-2">
              <Input
                id="new-name"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Ex.: João da Silva Santos"
                autoFocus
                maxLength={120}
                data-testid="edit-name-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={autoCorrect}
                title="Corrige automaticamente a capitalização (Title Case com preposições)"
                data-testid="edit-name-autocorrect"
              >
                <Wand2 className="w-4 h-4 mr-1" />
                Auto-corrigir
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Regras: cada palavra com inicial maiúscula, exceto preposições
              (de, da, do, dos, das, e). Espaços extras são removidos.
            </p>
          </div>

          {/* Preview da mudança */}
          {changed && (
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3" data-testid="edit-name-preview">
              <p className="text-[10px] uppercase text-amber-800 font-semibold tracking-wider mb-2">
                Preview da alteração
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground line-through break-all">
                  {currentName}
                </span>
                <ArrowRight className="w-4 h-4 text-amber-600 shrink-0" />
                <span className="text-sm font-semibold text-amber-900 break-all">
                  {value.trim()}
                </span>
              </div>
            </div>
          )}

          <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 flex gap-2">
            <AlertTriangle className="w-4 h-4 text-sky-600 mt-0.5 shrink-0" />
            <p className="text-xs text-sky-900">
              Esta ação atualiza também o nome nos <strong>certificados já
              emitidos</strong>. O próximo download regenera o PDF com o nome
              corrigido. A alteração fica registrada no log de auditoria.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="edit-name-cancel">
            Cancelar
          </Button>
          <Button
            onClick={save}
            disabled={saving || !changed}
            data-testid="edit-name-save"
          >
            {saving ? (<><RefreshCw className="w-4 h-4 animate-spin mr-1" /> Salvando…</>) : "Salvar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// AdminUsuarios (default export) — página de listagem de alunos
// ============================================================================
export default function AdminUsuarios() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [profileFilter, setProfileFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [details, setDetails] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [diagTarget, setDiagTarget] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagData, setDiagData] = useState(null);
  const [editNameTarget, setEditNameTarget] = useState(null);

  // Item 4 — ações sobre certificados no Diagnóstico
  const [selectedCerts, setSelectedCerts] = useState([]); // array de cert ids
  const [certAction, setCertAction] = useState(null); // { mode, certIds, single, label }
  const [actionReason, setActionReason] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [deleteStep2, setDeleteStep2] = useState(false);

  const refreshDiag = async () => {
    if (!diagTarget) return;
    try {
      const { data } = await api.get(`/admin/diagnostics/user-certificates/${diagTarget.id}`);
      setDiagData(data);
    } catch { /* silencioso */ }
  };

  const toggleCertSelect = (certId) => {
    setSelectedCerts((prev) =>
      prev.includes(certId) ? prev.filter((x) => x !== certId) : [...prev, certId]
    );
  };

  const openCertAction = (mode, certIds, label) => {
    setCertAction({ mode, certIds, single: certIds.length === 1, label });
    setActionReason("");
    setDeleteStep2(false);
  };

  const submitCertAction = async () => {
    if (!certAction) return;
    const { mode, certIds } = certAction;
    const reason = actionReason.trim();
    if ((mode === "block" || mode === "delete") && reason.length < 3) {
      toast.error("Informe o motivo (mínimo 3 caracteres).");
      return;
    }
    if (mode === "delete" && !deleteStep2) {
      setDeleteStep2(true);
      return;
    }
    setActionBusy(true);
    try {
      if (certIds.length === 1) {
        const id = certIds[0];
        if (mode === "block") await api.post(`/admin/certificates/${id}/block`, { reason });
        else if (mode === "unblock") await api.post(`/admin/certificates/${id}/unblock`, { reason: reason || null });
        else if (mode === "delete") await api.post(`/admin/certificates/${id}/delete`, { reason });
      } else {
        await api.post(`/admin/certificates/bulk-action`, { action: mode, cert_ids: certIds, reason });
      }
      toast.success(
        mode === "block" ? "Certificado(s) bloqueado(s)."
        : mode === "unblock" ? "Certificado(s) desbloqueado(s)."
        : "Certificado(s) excluído(s)."
      );
      setCertAction(null);
      setSelectedCerts([]);
      await refreshDiag();
      load(); // atualiza horas/cursos na listagem
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao executar a ação");
    } finally {
      setActionBusy(false);
    }
  };

  const certStatusBadge = (status) => {
    if (status === "bloqueado") return <Badge className="text-[10px] bg-rose-100 text-rose-900 border-rose-300">Bloqueado</Badge>;
    if (status === "cancelado") return <Badge className="text-[10px] bg-rose-100 text-rose-900 border-rose-300">Cancelado</Badge>;
    return <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-300">Ativo</Badge>;
  };

  const openDiag = async (u) => {
    setDiagTarget(u);
    setDiagLoading(true);
    setDiagData(null);
    setSelectedCerts([]);
    try {
      const { data } = await api.get(`/admin/diagnostics/user-certificates/${u.id}`);
      setDiagData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao gerar diagnóstico");
      setDiagTarget(null);
    } finally {
      setDiagLoading(false);
    }
  };

  const load = () => {
    const params = {};
    if (search) params.search = search;
    if (roleFilter && roleFilter !== "all") params.role = roleFilter;
    if (profileFilter && profileFilter !== "all") params.profile = profileFilter;
    api.get("/admin/users", { params }).then(r => setUsers(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const updateUser = async (id, changes) => {
    try {
      await api.put(`/admin/users/${id}`, changes);
      toast.success("Usuário atualizado");
      load();
      if (open?.id === id) setOpen(prev => ({ ...prev, ...changes }));
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const openDetails = async (u) => {
    setOpen(u);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/full`);
      setDetails(data);
    } catch { setDetails(null); }
  };

  return (
    <AdminLayout title="Usuários">
      <Card className="rounded-xl">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Buscar por nome, email, CPF, CEAP ID…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                data-testid="users-search"
              />
            </div>
            <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); }}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Todos os papéis" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos papéis</SelectItem>
                <SelectItem value="usuario_comum">Comum</SelectItem>
                <SelectItem value="aluno_validado">Aluno validado</SelectItem>
                <SelectItem value="aluno_premium">Aluno premium</SelectItem>
                <SelectItem value="operador_evento">Operador evento</SelectItem>
                <SelectItem value="comercial">Comercial</SelectItem>
                <SelectItem value="admin_master">Admin master</SelectItem>
              </SelectContent>
            </Select>
            <Select value={profileFilter} onValueChange={(v) => { setProfileFilter(v); }}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Todos perfis" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos perfis</SelectItem>
                <SelectItem value="legislativo">Legislativo</SelectItem>
                <SelectItem value="licitacoes">Licitações</SelectItem>
                <SelectItem value="ambos">Ambos</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={load} data-testid="users-apply">Filtrar</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl mt-4">
        <CardContent className="p-0 overflow-x-auto">
          <Table data-testid="admin-users-table">
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>CEAP ID</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Perfil</TableHead>
                <TableHead>Papel</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">{u.full_name}</TableCell>
                  <TableCell className="font-mono-id text-xs">{u.ceap_id}</TableCell>
                  <TableCell className="text-xs">{u.email}</TableCell>
                  <TableCell><Badge variant="secondary" className="capitalize">{u.profile}</Badge></TableCell>
                  <TableCell><Badge variant="outline" className="capitalize">{u.role.replace("_", " ")}</Badge></TableCell>
                  <TableCell>
                    {u.is_blocked ? (
                      <Badge className="bg-rose-100 text-rose-800 border-rose-200">Bloqueado</Badge>
                    ) : u.is_validated ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Validado</Badge>
                    ) : (
                      <Badge variant="outline">Pendente</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDetails(u)}
                        title="Ver detalhes"
                        data-testid={`user-view-${u.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {!u.is_validated && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => updateUser(u.id, { is_validated: true, role: "aluno_validado" })}
                          title="Validar cadastro"
                          data-testid={`user-validate-${u.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDiag(u)}
                        title="Diagnóstico de certificados"
                        data-testid={`user-diag-${u.id}`}
                      >
                        <Stethoscope className="w-4 h-4 text-sky-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditNameTarget(u)}
                        title="Corrigir nome do aluno"
                        data-testid={`user-edit-name-${u.id}`}
                      >
                        <Pencil className="w-4 h-4 text-indigo-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setResetTarget(u)}
                        title="Resetar senha"
                        data-testid={`user-reset-${u.id}`}
                      >
                        <KeyRound className="w-4 h-4 text-amber-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => updateUser(u.id, { is_blocked: !u.is_blocked })}
                        title={u.is_blocked ? "Desbloquear" : "Bloquear"}
                        data-testid={`user-block-${u.id}`}
                      >
                        {u.is_blocked ? <Shield className="w-4 h-4 text-emerald-600" /> : <Ban className="w-4 h-4 text-rose-600" />}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-10">
                    Nenhum usuário encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Diálogo de detalhes / edição */}
      <Dialog open={!!open} onOpenChange={(o) => { if (!o) { setOpen(null); setDetails(null); } }}>
        <DialogContent className="max-w-[640px] rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{open?.full_name}</DialogTitle></DialogHeader>
          {open && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-muted-foreground text-xs">CEAP ID</p><p className="font-mono-id">{open.ceap_id}</p></div>
                <div><p className="text-muted-foreground text-xs">Nível</p><p>{open.level}</p></div>
                <div><p className="text-muted-foreground text-xs">Cargo</p><p>{open.cargo || "—"}</p></div>
                <div><p className="text-muted-foreground text-xs">Órgão</p><p>{open.orgao || "—"}</p></div>
                <div><p className="text-muted-foreground text-xs">Cidade/UF</p><p>{open.cidade}/{open.estado}</p></div>
                <div><p className="text-muted-foreground text-xs">Cursos / Horas</p><p>{open.courses_completed} / {open.hours_accumulated}h</p></div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={open.role} onValueChange={(v) => updateUser(open.id, { role: v })}>
                  <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usuario_comum">Comum</SelectItem>
                    <SelectItem value="aluno_validado">Aluno validado</SelectItem>
                    <SelectItem value="aluno_premium">Aluno premium</SelectItem>
                    <SelectItem value="operador_evento">Operador evento</SelectItem>
                    <SelectItem value="comercial">Comercial</SelectItem>
                    <SelectItem value="admin_master">Admin master</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={open.level} onValueChange={(v) => updateUser(open.id, { level: v })}>
                  <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Aluno CEAP">Aluno CEAP</SelectItem>
                    <SelectItem value="Aluno Recorrente">Aluno Recorrente</SelectItem>
                    <SelectItem value="Especialista CEAP">Especialista CEAP</SelectItem>
                    <SelectItem value="Embaixador CEAP">Embaixador CEAP</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setOpen(null); setDetails(null); setResetTarget(open); }}
                  className="ml-auto"
                  data-testid="user-detail-reset-pwd"
                >
                  <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Resetar senha
                </Button>
              </div>
              {details && (
                <>
                  <div>
                    <p className="font-medium mt-3">Certificados ({details.certificates.length})</p>
                    <ul className="text-xs text-muted-foreground mt-1 space-y-1 max-h-24 overflow-y-auto">
                      {details.certificates.map(c => <li key={c.id}>{c.course_name} — <span className="font-mono-id">{c.validation_code}</span></li>)}
                    </ul>
                  </div>
                  <AttendanceSection attendance={details.attendance} />
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo de reset de senha */}
      <ResetStudentPasswordDialog
        user={resetTarget}
        open={!!resetTarget}
        onClose={() => setResetTarget(null)}
      />

      {/* Diálogo de edição de nome */}
      <EditNameDialog
        target={editNameTarget}
        onClose={() => setEditNameTarget(null)}
        onSaved={(updatedUser) => {
          // Atualiza otimisticamente o usuário na lista (sem novo GET)
          setUsers((prev) => prev.map((u) => (u.id === updatedUser.id ? { ...u, ...updatedUser } : u)));
          // Se o diálogo de detalhes estiver aberto para o mesmo usuário, atualiza também
          if (open?.id === updatedUser.id) {
            setOpen((prev) => ({ ...prev, ...updatedUser }));
          }
        }}
      />

      {/* Diálogo de diagnóstico de certificados */}
      <Dialog
        open={!!diagTarget}
        onOpenChange={(o) => { if (!o) { setDiagTarget(null); setDiagData(null); } }}
      >
        <DialogContent className="max-w-[720px] rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="diagnostic-dialog">
          <DialogHeader>
            <div className="w-11 h-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center mb-1">
              <Stethoscope className="w-5 h-5" />
            </div>
            <DialogTitle className="font-display">Diagnóstico de certificados</DialogTitle>
            <DialogDescription>
              Relatório detalhado dos certificados de{" "}
              <span className="font-medium text-[hsl(var(--primary))]">{diagTarget?.full_name}</span>.
              Use para investigar quando um aluno reporta que não consegue baixar o PDF ou o código não é encontrado na validação.
            </DialogDescription>
          </DialogHeader>

          {diagLoading && (
            <div className="text-center py-6 text-sm text-muted-foreground">Gerando diagnóstico…</div>
          )}

          {diagData && (
            <div className="space-y-4 text-sm">
              {/* Resumo do aluno */}
              <Card className="bg-[hsl(var(--secondary))]/60">
                <CardContent className="p-3 space-y-1">
                  <div className="flex justify-between gap-3">
                    <span className="text-xs text-muted-foreground">CEAP ID</span>
                    <span className="font-mono-id text-xs">{diagData.user.ceap_id}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-xs text-muted-foreground">Horas acumuladas</span>
                    <span className="text-xs font-medium">{diagData.user.hours_accumulated}h</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-xs text-muted-foreground">Cursos concluídos</span>
                    <span className="text-xs font-medium">{diagData.user.courses_completed}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-xs text-muted-foreground">Sessões de presença</span>
                    <span className="text-xs font-medium">{diagData.attendance_count}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Certificados existentes */}
              <div>
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <p className="font-semibold text-sm">
                    Certificados emitidos ({diagData.certificates.length})
                  </p>
                  {selectedCerts.length > 0 && (
                    <div className="flex items-center gap-1.5" data-testid="cert-bulk-bar">
                      <span className="text-xs text-muted-foreground mr-1">{selectedCerts.length} selecionado(s)</span>
                      <Button size="sm" variant="outline" className="h-7 text-amber-800 border-amber-300 hover:bg-amber-50"
                        onClick={() => openCertAction("block", selectedCerts, "Bloquear selecionados")} data-testid="cert-bulk-block">
                        <Lock className="w-3.5 h-3.5 mr-1" /> Bloquear
                      </Button>
                      <Button size="sm" variant="outline" className="h-7"
                        onClick={() => openCertAction("unblock", selectedCerts, "Desbloquear selecionados")} data-testid="cert-bulk-unblock">
                        <Unlock className="w-3.5 h-3.5 mr-1" /> Desbloquear
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-rose-700 border-rose-300 hover:bg-rose-50"
                        onClick={() => openCertAction("delete", selectedCerts, "Excluir selecionados")} data-testid="cert-bulk-delete">
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Excluir
                      </Button>
                    </div>
                  )}
                </div>
                {diagData.certificates.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Nenhum certificado registrado.</p>
                ) : (
                  <div className="space-y-2">
                    {diagData.certificates.map((c) => {
                      const problems = [];
                      if (!c.validation_code) problems.push("Sem validation_code");
                      if (!c.validation_code_resolvable) problems.push("Código NÃO resolve na validação");
                      if (c.is_locked) problems.push("Aluno precisa responder pesquisa");
                      const isBlocked = c.status === "bloqueado";
                      const isInactive = c.status === "bloqueado" || c.status === "cancelado";
                      const checked = selectedCerts.includes(c.id);
                      return (
                        <Card key={c.id} className={isInactive ? "border-rose-200 bg-rose-50/30" : (problems.length > 0 ? "border-amber-300 bg-amber-50/40" : "border-emerald-200 bg-emerald-50/30")} data-testid={`diag-cert-${c.id}`}>
                          <CardContent className="p-3 space-y-1">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-start gap-2 min-w-0">
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={() => toggleCertSelect(c.id)}
                                  className="mt-0.5"
                                  data-testid={`diag-cert-check-${c.id}`}
                                />
                                <p className="text-xs font-medium text-[hsl(var(--primary))] line-clamp-2">{c.course_name}</p>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {certStatusBadge(c.status)}
                                {problems.length === 0 && !isInactive ? (
                                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                ) : (
                                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                                )}
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                              <span>Código: <span className="font-mono-id">{c.validation_code || "—"}</span></span>
                              <span>Emitido: {c.is_manual_issue ? "manual" : (c.is_auto_issued ? "automático" : "legado")}</span>
                              <span>Resolve na validação: <strong className={c.validation_code_resolvable ? "text-emerald-700" : "text-rose-700"}>{c.validation_code_resolvable ? "sim" : "NÃO"}</strong></span>
                              <span>Reconhecido pelo aluno: {c.acknowledged_at ? "sim" : "não"}</span>
                              <span>Pesquisa exigida: {c.survey_required ? "sim" : "não"}</span>
                              <span>Pesquisa respondida: {c.survey_completed ? "sim" : "não"}</span>
                            </div>
                            {problems.length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {problems.map((p, i) => (
                                  <Badge key={i} className="text-[10px] bg-amber-100 text-amber-900 border-amber-300">{p}</Badge>
                                ))}
                              </div>
                            )}
                            <div className="flex items-center justify-end gap-1 pt-1">
                              {isBlocked ? (
                                <Button size="sm" variant="ghost" className="h-7 text-xs"
                                  onClick={() => openCertAction("unblock", [c.id], "Desbloquear certificado")} data-testid={`cert-unblock-${c.id}`}>
                                  <Unlock className="w-3.5 h-3.5 mr-1" /> Desbloquear
                                </Button>
                              ) : (
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-amber-800 hover:bg-amber-50"
                                  onClick={() => openCertAction("block", [c.id], "Bloquear certificado")} data-testid={`cert-block-${c.id}`}>
                                  <Lock className="w-3.5 h-3.5 mr-1" /> Bloquear
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-700 hover:bg-rose-50"
                                onClick={() => openCertAction("delete", [c.id], "Excluir certificado")} data-testid={`cert-delete-${c.id}`}>
                                <Trash2 className="w-3.5 h-3.5 mr-1" /> Excluir
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Cursos com attendance mas sem certificado */}
              {diagData.missing_certificates.length > 0 && (
                <div>
                  <p className="font-semibold text-sm mb-2 flex items-center gap-1.5">
                    <XCircle className="w-4 h-4 text-rose-600" />
                    Cursos com presença mas SEM certificado ({diagData.missing_certificates.length})
                  </p>
                  <div className="space-y-2">
                    {diagData.missing_certificates.map((m) => {
                      const severity = m.reason === "auto_issue_failed" ? "rose" : (m.reason === "below_min_frequency" ? "amber" : "slate");
                      const bg = severity === "rose" ? "border-rose-300 bg-rose-50/50" : (severity === "amber" ? "border-amber-300 bg-amber-50/50" : "border-slate-300 bg-slate-50/50");
                      return (
                        <Card key={m.course_id} className={bg}>
                          <CardContent className="p-3 space-y-1">
                            <p className="text-xs font-medium text-[hsl(var(--primary))]">{m.course_name || m.course_id}</p>
                            <p className="text-[11px] text-muted-foreground">
                              {m.hours_attended_total !== undefined && (
                                <>Freq: <strong>{m.attendance_percent}%</strong> ({m.hours_attended_total}h de {m.effective_workload}h efetivas). Mínimo: {m.min_attendance_percent}%. Terminou: {m.course_ended ? "sim" : "não"}.</>
                              )}
                            </p>
                            <p className="text-[11px] font-medium">
                              <Badge className={
                                severity === "rose" ? "bg-rose-100 text-rose-900 border-rose-300" :
                                (severity === "amber" ? "bg-amber-100 text-amber-900 border-amber-300" : "bg-slate-100 text-slate-800")
                              }>
                                {m.reason}
                              </Badge>{" "}
                              <span className="text-muted-foreground">{m.detail}</span>
                            </p>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Raw JSON copiável para suporte técnico */}
              <details className="text-[11px]">
                <summary className="cursor-pointer text-muted-foreground hover:text-[hsl(var(--primary))]">
                  Ver JSON completo (para envio ao suporte técnico)
                </summary>
                <pre className="mt-2 p-2 bg-slate-900 text-slate-100 rounded overflow-x-auto text-[10px] leading-tight">
                  {JSON.stringify(diagData, null, 2)}
                </pre>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={async () => {
                    const ok = await copyToClipboard(JSON.stringify(diagData, null, 2));
                    toast[ok ? "success" : "error"](ok ? "JSON copiado" : "Falha ao copiar");
                  }}
                  data-testid="diag-copy-json"
                >
                  <Copy className="w-3 h-3 mr-1" /> Copiar JSON
                </Button>
              </details>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: ação sobre certificado(s) — bloquear / desbloquear / excluir */}
      <Dialog open={!!certAction} onOpenChange={(o) => { if (!o) { setCertAction(null); setDeleteStep2(false); } }}>
        <DialogContent className="rounded-2xl max-w-[520px]">
          <DialogHeader>
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-1 ${certAction?.mode === "delete" ? "bg-rose-100 text-rose-700" : certAction?.mode === "unblock" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
              {certAction?.mode === "delete" ? <Trash2 className="w-5 h-5" /> : certAction?.mode === "unblock" ? <Unlock className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
            </div>
            <DialogTitle className="font-display">{certAction?.label}</DialogTitle>
            <DialogDescription>
              {certAction?.certIds?.length > 1
                ? `Ação em ${certAction.certIds.length} certificados selecionados.`
                : "Ação sobre 1 certificado."}{" "}
              {certAction?.mode === "delete"
                ? "A exclusão é definitiva e recalcula as horas/cursos do aluno."
                : certAction?.mode === "block"
                ? "O certificado deixa de ser baixável e retorna 'bloqueado' na validação pública. É reversível."
                : "O certificado volta a ficar disponível e a somar horas."}
            </DialogDescription>
          </DialogHeader>

          {(certAction?.mode === "block" || certAction?.mode === "delete") && (
            <div className="space-y-1.5">
              <Label>Motivo {certAction?.mode === "delete" ? "da exclusão" : "do bloqueio"} *</Label>
              <Textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Descreva o motivo (registrado em Logs de Acesso)"
                rows={3}
                data-testid="cert-action-reason"
              />
            </div>
          )}
          {certAction?.mode === "unblock" && (
            <div className="space-y-1.5">
              <Label>Observação (opcional)</Label>
              <Textarea value={actionReason} onChange={(e) => setActionReason(e.target.value)} rows={2} data-testid="cert-action-reason" />
            </div>
          )}

          {certAction?.mode === "delete" && deleteStep2 && (
            <div className="flex items-start gap-2 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-md p-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Confirmação final: esta ação NÃO pode ser desfeita. Clique novamente em "Excluir definitivamente".</span>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setCertAction(null); setDeleteStep2(false); }}>Cancelar</Button>
            <Button
              className={certAction?.mode === "delete" ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}
              onClick={submitCertAction}
              disabled={actionBusy}
              data-testid="cert-action-confirm"
            >
              {certAction?.mode === "delete"
                ? (deleteStep2 ? "Excluir definitivamente" : "Excluir")
                : certAction?.mode === "unblock" ? "Desbloquear" : "Bloquear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
